import React, {useCallback, useRef, useState} from "react";
import {Portal} from "@chakra-ui/react";
import {useHotkeys} from "react-hotkeys-hook";
import {EntitySearchItem} from "./EntitySearchItem";
import {AnimatePresence, motion} from "framer-motion";

interface EntitySearchModalProps<T> {
  items: T[];
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  onSelect: (id: T) => void;
  searchText: string;
  onSearchTextChange: (searchText: string) => void;
  renderDescription: (item: T) => React.ReactNode;
  placeholder: string;
}

export function EntitySearchModal<T>({
  items,
  isOpen,
  onClose,
  onOpen,
  onSelect,
  searchText,
  onSearchTextChange,
  renderDescription,
  placeholder,
}: EntitySearchModalProps<T>) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const close = useCallback(() => {
    onClose();
    setFocusedIndex(0);
    onSearchTextChange("");
  }, [onClose, onSearchTextChange]);

  useHotkeys("ctrl+k, cmd+k", (e: KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen();
  });

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      } else if (event.key === "ArrowDown") {
        setFocusedIndex((prevIndex) => (prevIndex + 1) % items.length);
      } else if (event.key === "ArrowUp") {
        setFocusedIndex(
          (prevIndex) => (prevIndex - 1 + items.length) % items.length
        );
      } else if (event.key === "Enter") {
        onSelect(items[focusedIndex]);
        close();
      }
    },
    [close, items, onSelect, focusedIndex]
  );

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
          <Portal>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            style={{
              position: "fixed",
              display: "flex",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 9999,
              alignItems: "flex-start",
              justifyContent: "center",
            }}
            onClick={close}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "3rem",
                marginLeft: "8px",
                marginRight: "8px",
                backgroundColor: "#fff",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                maxWidth: "768px",
                maxHeight: "40vh",
                overflow: "hidden",
                width: "100%",
                borderRadius: "6px",
                borderStyle: "solid",
                borderWidth: "1px",
              }}
            >
              <input
                  ref={searchRef}
                  autoFocus
                  placeholder={placeholder}
                  value={searchText}
                  onKeyDown={onKeyDown}
                  onChange={(e) => onSearchTextChange(e.target.value)}
                  style={{
                    minHeight: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    margin: '8px',
                  }}
              />
              <ul
                style={{
                  overflowY: "auto",
                  width: "100%",
                  paddingBottom: "8px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
              >
                {items.map((item, index) => (
                  <EntitySearchItem<T>
                    key={index}
                    item={item}
                    onClick={() => {
                      onSelect(item);
                      close();
                    }}
                    focusedIndex={focusedIndex}
                    index={index}
                    renderDescription={renderDescription}
                  />
                ))}
              </ul>
            </div>
          </motion.div>
          </Portal>
      )}
    </AnimatePresence>
  );
}
